import { 
  Radar, 
  RadarChart, 
  PolarGrid, 
  PolarAngleAxis, 
  PolarRadiusAxis,
  ResponsiveContainer,
  Tooltip
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FundamentalDueDiligence } from '@/hooks/useDeepAnalysis';
import { cn } from '@/lib/utils';

interface DDRadarChartProps {
  dd: FundamentalDueDiligence;
}

interface RadarDataPoint {
  category: string;
  score: number;
  fullMark: 100;
}

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const score = data.score;
    const getScoreLabel = (s: number) => {
      if (s >= 70) return { label: 'Strong', color: 'text-green-500' };
      if (s >= 50) return { label: 'Moderate', color: 'text-yellow-500' };
      if (s >= 30) return { label: 'Weak', color: 'text-orange-500' };
      return { label: 'Poor', color: 'text-red-500' };
    };
    const scoreInfo = getScoreLabel(score);
    
    return (
      <div className="bg-background border border-border rounded-lg shadow-lg p-3 min-w-[140px]">
        <p className="text-sm font-medium text-foreground mb-1">{data.category}</p>
        <div className="flex items-center justify-between gap-4">
          <span className={cn("font-bold text-lg", scoreInfo.color)}>{score}/100</span>
          <span className={cn("text-xs", scoreInfo.color)}>{scoreInfo.label}</span>
        </div>
      </div>
    );
  }
  return null;
};

export function DDRadarChart({ dd }: DDRadarChartProps) {
  const radarData: RadarDataPoint[] = [
    { category: 'Project', score: dd.projectFundamentals?.score || 0, fullMark: 100 },
    { category: 'Tokenomics', score: dd.tokenomics?.score || 0, fullMark: 100 },
    { category: 'Team', score: dd.teamAndExecution?.score || 0, fullMark: 100 },
    { category: 'Tech', score: dd.technologyAndSecurity?.score || 0, fullMark: 100 },
    { category: 'Adoption', score: dd.adoptionMetrics?.score || 0, fullMark: 100 },
    { category: 'Market', score: dd.marketOpportunity?.score || 0, fullMark: 100 },
    { category: 'Valuation', score: dd.valuationAnalysis?.score || 0, fullMark: 100 },
    { category: 'Regulatory', score: dd.regulatoryAndRisks?.score || 0, fullMark: 100 },
    { category: 'Community', score: dd.communityAndEcosystem?.score || 0, fullMark: 100 },
  ];

  // Calculate average score
  const avgScore = Math.round(radarData.reduce((sum, d) => sum + d.score, 0) / radarData.length);
  
  // Determine fill color based on average
  const getFillColor = (score: number) => {
    if (score >= 70) return 'hsl(142, 76%, 36%)'; // green
    if (score >= 50) return 'hsl(48, 96%, 53%)'; // yellow
    if (score >= 30) return 'hsl(25, 95%, 53%)'; // orange
    return 'hsl(0, 84%, 60%)'; // red
  };

  const fillColor = getFillColor(avgScore);
  const strokeColor = fillColor;

  return (
    <Card className="border-border/50">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          DD Score Distribution
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart cx="50%" cy="50%" outerRadius="75%" data={radarData}>
              <PolarGrid 
                stroke="hsl(var(--border))" 
                strokeOpacity={0.5}
              />
              <PolarAngleAxis 
                dataKey="category" 
                tick={{ 
                  fill: 'hsl(var(--muted-foreground))', 
                  fontSize: 11,
                  fontWeight: 500
                }}
                tickLine={false}
              />
              <PolarRadiusAxis 
                angle={90} 
                domain={[0, 100]}
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                tickCount={5}
                axisLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Radar
                name="DD Score"
                dataKey="score"
                stroke={strokeColor}
                fill={fillColor}
                fillOpacity={0.35}
                strokeWidth={2}
                dot={{
                  r: 4,
                  fill: strokeColor,
                  strokeWidth: 0
                }}
                activeDot={{
                  r: 6,
                  fill: strokeColor,
                  stroke: 'hsl(var(--background))',
                  strokeWidth: 2
                }}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>
        
        {/* Legend */}
        <div className="flex flex-wrap justify-center gap-4 mt-4 text-xs">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span className="text-muted-foreground">Strong (70+)</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <span className="text-muted-foreground">Moderate (50-69)</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-orange-500" />
            <span className="text-muted-foreground">Weak (30-49)</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <span className="text-muted-foreground">Poor (&lt;30)</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
